/**
 * hrDemoSeed.ts
 *
 * One-click demo cycle seeder. Creates a brand-new hr_v1 ReviewCycle and
 * runs launchStep1 + launchStep2 with hard-coded sample data so HR can
 * smoke-test the full workflow without composing JSON.
 *
 * Use case: clicking "Create demo cycle" on the /hr-v1 index page during
 * staging / local validation.
 */

import {
  doc, setDoc, getDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  launchStep1, launchStep2, type LaunchStep1Input, type LaunchStep2Input,
} from './hrCycleLauncher';

export interface DemoSeedResult {
  cycleId: string;
  cycleName: string;
  selfUrls:    Array<{ employeeId: string; url: string }>;
  managerUrls: Array<{ employeeId: string; url: string }>;
  reviewer360Urls: Array<{
    revieweeId: string;
    reviewerEmail: string;
    relationship: string;
    url: string;
  }>;
}

const DEMO_EMPLOYEES: LaunchStep1Input['employees'] = [
  {
    employeeId:      'demo-emp-001',
    employeeEmail:   'andi.demo@test.local',
    employeeName:    'Andi (Demo Employee)',
    supervisorId:    'demo-mgr-001',
    supervisorEmail: 'wawan.demo@test.local',
    supervisorName:  'Wawan (Demo Manager)',
    level:           'general',
    okrItems: [
      { id: 'kr1', name: 'Launch new portal',     target: 'Done by Q2' },
      { id: 'kr2', name: 'Reduce churn 20%',      target: '20%' },
    ],
    kpiItems: [
      { id: 'kpi1', name: 'Sales quota',          target: '100' },
      { id: 'kpi2', name: 'Customer response < 2h', target: '< 2h' },
    ],
  },
];

const DEMO_360_PANEL: LaunchStep2Input['reviewees'] = [
  {
    revieweeId:   'demo-emp-001',
    revieweeName: 'Andi (Demo Employee)',
    reviewers: [
      { reviewerId: 'demo-mgr-001', reviewerEmail: 'wawan.demo@test.local', reviewerName: 'Wawan',         relationship: 'manager' },
      { reviewerId: 'demo-peer-1',  reviewerEmail: 'budi.demo@test.local',  reviewerName: 'Budi (Demo)',   relationship: 'peer' },
      { reviewerId: 'demo-peer-2',  reviewerEmail: 'cici.demo@test.local',  reviewerName: 'Cici (Demo)',   relationship: 'peer' },
      { reviewerId: 'demo-peer-3',  reviewerEmail: 'doni.demo@test.local',  reviewerName: 'Doni (Demo)',   relationship: 'peer' },
    ],
  },
];

export interface CreateDemoCycleInput {
  cycleName?: string;
  cycleId?: string;
  templateId?: string;
  /** Caller userId for createdBy audit. */
  createdBy: string;
}

/**
 * Idempotent: re-running with the same cycleId returns the existing cycle's
 * fresh token URLs (launcher itself is idempotent on the submission docs).
 */
export async function createDemoCycle(input: CreateDemoCycleInput): Promise<DemoSeedResult> {
  const now = new Date();
  // Compact id: yyyymmdd-hhmm so cycles are alphabetisable but readable
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const cycleId   = input.cycleId   ?? `demo-${stamp}`;
  // Friendly display: "Demo Cycle · H1 2026"
  const half = now.getMonth() < 6 ? 'H1' : 'H2';
  const cycleName = input.cycleName ?? `Demo Cycle · ${half} ${now.getFullYear()}`;
  const templateId = input.templateId ?? 'default-hr-v1';

  // Create the cycle doc (idempotent)
  const ref = doc(db, 'reviewCycles', cycleId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    const nowTs = Timestamp.now();
    const endTs = Timestamp.fromDate(new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000)); // +60d
    await setDoc(ref, {
      name:              cycleName,
      year:              now.getFullYear(),
      period:            'H1',
      templateId,
      status:            'active',
      prepWindowStart:   nowTs,
      startDate:         nowTs,
      lockDate:          endTs,
      endDate:           endTs,
      createdBy:         input.createdBy,
      createdAt:         serverTimestamp(),
      frameworkVersion:  'hr_v1',
      hrFrameworkTemplateId: templateId,
    });
  }

  // Step 1
  const step1 = await launchStep1({
    cycleId,
    templateId,
    employees: DEMO_EMPLOYEES,
    advanceCycleStep: true,
  });

  // Step 2
  const step2 = await launchStep2({
    cycleId,
    reviewees: DEMO_360_PANEL,
    advanceCycleStep: true,
  });

  return {
    cycleId,
    cycleName,
    selfUrls: step1.selfTokens.map((t, i) => ({
      employeeId: step1.submissions[i].employeeId,
      url:        t.url,
    })),
    managerUrls: step1.managerTokens.map((t, i) => ({
      employeeId: step1.submissions[i].employeeId,
      url:        t.url,
    })),
    reviewer360Urls: step2.panelEntries.map(p => ({
      revieweeId:    p.revieweeId,
      reviewerEmail: p.reviewerEmail,
      relationship:  p.relationship,
      url:           p.url,
    })),
  };
}
